import { type ReactNode, useState } from 'react';

import type { RouteRef, SubRouteRef } from '@backstage/core-plugin-api';

import { Page } from '@spotify/confidence-common-react';
import {
  FilterBar,
  FiltersProvider,
  type ViewMode,
  ViewModeToggle,
} from '@spotify/confidence-core-react';
import {
  FAVORITES_FILTER,
  useFavoritesFilter,
} from '@spotify/plugin-confidence-collections-react';

import { useCreatableWorkflowExtensions } from '../../hooks';
import { CreateExperimentMenu } from './CreateExperimentMenu';

type FiltersProviderProps = Parameters<typeof FiltersProvider>[0];

export type ExperimentsPageLayoutProps = {
  routeRef: RouteRef | SubRouteRef;
  storageKey: string;
  filters: FiltersProviderProps['filters'];
  quickFilterIds?: FiltersProviderProps['quickFilterIds'];
  defaultFilters?: FiltersProviderProps['defaultFilters'];
  renderTable: (props: { mode: ViewMode }) => ReactNode;
};

export const ExperimentsPageLayout = ({
  routeRef,
  storageKey,
  filters,
  quickFilterIds,
  defaultFilters,
  renderTable,
}: ExperimentsPageLayoutProps) => {
  const [mode, setMode] = useState<ViewMode>('Table');
  const creatableExtensions = useCreatableWorkflowExtensions();

  const favoritesFilter = useFavoritesFilter();

  return (
    <Page.Root routeRef={routeRef}>
      <Page.Wrapper
        component={FiltersProvider}
        storageKey={storageKey}
        searchField="displayName"
        filters={[favoritesFilter, ...(filters ?? [])]}
        quickFilterIds={[FAVORITES_FILTER.id, ...(quickFilterIds ?? [])]}
        defaultFilters={defaultFilters}
      />
      <Page.Content scrollButton>
        <Page.ContentActionBar>
          <ViewModeToggle mode={mode} onModeChange={setMode}  data-track-source-anchor="206c713b69;experiments-page-layout-view-mode-toggle"/>
          <FilterBar />
          {creatableExtensions.length > 0 && (
            <CreateExperimentMenu extensions={creatableExtensions} />
          )}
        </Page.ContentActionBar>
        <Page.ContentMain>{renderTable({ mode })}</Page.ContentMain>
      </Page.Content>
    </Page.Root>
  );
};
